import { NgFor, NgIf, SlicePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, makeStateKey, OnDestroy, OnInit, signal, TransferState } from "@angular/core";
import { MetaDefinition } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription, first } from "rxjs";
import { TipoTarjeta } from "@src/app/models/enums/tipoTarjeta.enum";
import { KebabCasePipe } from "src/app/pipes/kebab-case.pipe";
import { ConfigService } from "src/app/servicios/config/config.service";
import { GfkService } from "src/app/servicios/gfk/gfk.service";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { SeoService } from "src/app/servicios/seo/seo.service";
import { TitleWrapperService } from "src/app/servicios/title-wrapper/title-wrapper.service";
import { NoticiasService } from "../../servicios/noticias/noticias.service";
import { PublicidadService } from "../../servicios/publicidad/publicidad.service";
import { NoticiaListadoComponent } from "../noticia-listado/noticia-listado.component";
import { TarjetaInfoAutorComponent } from "../tarjeta-redes/tarjeta-info-autor.component";
import { ReelsService } from "@src/app/servicios/reels.service";
import { CanonicalService } from "@src/app/servicios/canonical/canonical.service";
import { FooterService } from "@src/app/servicios/footer.service";

const TRANSFER_KEY = makeStateKey<any>("listado-noticias");

@Component({
  selector: "app-vista-noticias",
  templateUrl: "./vista-noticias.component.html",
  styleUrls: ["./vista-noticias.component.css"],
  imports: [TarjetaInfoAutorComponent, NoticiaListadoComponent, SlicePipe, NgIf, NgFor],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VistaNoticiasComponent implements OnInit, OnDestroy {
  videos = signal([]);
  id: any;
  url;
  tipoTarjeta = TipoTarjeta.Redes;
  publicidadSeccion1: any;
  publicidadSeccion2: any;
  publicidadSeccion3: any;
  publicidadSeccion4: any;
  constantesGlobales;
  rSub: Subscription;

  constructor(
    private activatedRoute: ActivatedRoute,
    private noticiasService: NoticiasService,
    private publiService: PublicidadService,
    private titleWrapperService: TitleWrapperService,
    private router: Router,
    private kebabPipe: KebabCasePipe,
    private servicioSeo: SeoService,
    private platformService: PlatformService,
    private gfkService: GfkService,
    private configService: ConfigService,
    private transferState: TransferState,
    private reelsService: ReelsService,
    private readonly canonicalService: CanonicalService,
    private readonly footerService: FooterService,
  ) {
    this.reelsService.sethideOrientation(true);
  }

  ngOnInit(): void {
    this.footerService.isPaginationFinished.set(true);
    this.constantesGlobales = this.configService.getConstantesGlobales();

    this.rSub = this.activatedRoute.params.subscribe((parms: any) => {
      const { id, title } = parms;
      const data = this.activatedRoute?.snapshot?.data;

      this.url = data.url;
      this.id = id || data.forceTitle;
      const sectionTitle = data.forceTitle || title || id;
      if (!this.id || !this.url) {
        this.router.navigate(["404"]);
        return;
      }

      this.getSection(this.id, sectionTitle);
    });
  }

  ngOnDestroy(): void {
    if (this.rSub) this.rSub.unsubscribe();
    this.servicioSeo.borraScript("PersonStructData");
    this.transferState.remove(TRANSFER_KEY);
    this.reelsService.sethideOrientation(false);
  }

  getSection(id, title) {
    if (!this.platformService.isBrowser()) {
      this.fetchNewsAndStoreKey(id, title);
    } else {
      if (this.transferState.hasKey(TRANSFER_KEY) && this.transferState.get<any>(TRANSFER_KEY, null).idVideo === title.idVideo) {
        const news = this.transferState.get<any>(TRANSFER_KEY, null);
        this.transferState.remove(TRANSFER_KEY);
        this.setNews(news, title);
      } else {
        this.fetchNewsAndStoreKey(id, title);
      }
    }
  }

  fetchNewsAndStoreKey(id, title) {
    const idAmbito = this.constantesGlobales.idAmbito;
    const lastNews = this.noticiasService.getUltimasNoticias(31, this.url, id, idAmbito);
    if (lastNews) {
      lastNews.pipe(first()).subscribe({
        next: (result) => {
          this.setNews(result, title);
          if (!this.platformService.isBrowser()) {
            this.transferState.set(TRANSFER_KEY, result);
          }
        },
        error: () => {
          this.router.navigate(["404"]);
        },
      });
    }
  }

  setNews(news, title) {
    this.initGfk(title);

    this.videos.set(news);
    this.getPubliByConfig();
    if (this.url == "autor") {
      this.servicioSeo.insertaScriptPerson(this.id);
    }
    this.validarUrl(this.videos());
    const webTitle = this.titleCaseWord(title?.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ0-9 ]/g, " ") + " - " + this.constantesGlobales.siteName);
    this.titleWrapperService.setTitle(webTitle);
    this.updateMetaTags(title);
  }

  validarUrl(data): void {
    if (data.length == 0) {
      this.router.navigateByUrl("/404");
    } else {
      const url = this.platformService.getLocationPathname().split("/");
      let slug = "";
      let id = 0;

      if (url[1] == "seccion" || url[1] == "canal") {
        slug = this.kebabPipe.transform(data[0].nombreSubcanal);
        id = data[0].idSubcanal;
      } else if (url[1] == "autor") {
        if (url[2] == "tribuna") {
          return;
        }
        slug = this.kebabPipe.transform(data[0].nombre_autor);
        id = data[0].id_autor;
      } else {
        return;
      }
      if (url.pop() !== slug) {
        this.router.navigateByUrl("/" + url[1] + "/" + id + "/" + slug);
      }
    }
  }

  getPubliByConfig(): void {
    const publi = this.publiService.getPubli();
    if (publi) {
      publi.pipe(first()).subscribe((result) => {
        if (result != null) {
          const arrayPublicidadSeccion = result.imagenes.filter((item) => item.idPosicion === "10"); // title noticia
          const arrayPublicidadSeccionLatInterior = result.imagenes.filter((item) => item.idPosicion === "11"); // Lateral interior noticia
          const arrayPublicidadSeccionLateral = result.imagenes.filter((item) => item.idPosicion === "11"); // lateral
          const arrayPublicidadSeccionSuperior = result.imagenes.filter((item) => item.idPosicion === "16");

          let numPubli = Math.floor(Math.random() * arrayPublicidadSeccion.length - 1 + 1);
          this.publicidadSeccion1 = arrayPublicidadSeccion[numPubli];

          numPubli = Math.floor(Math.random() * arrayPublicidadSeccionLatInterior.length - 1 + 1);
          this.publicidadSeccion2 = arrayPublicidadSeccionLatInterior[numPubli];

          numPubli = Math.floor(Math.random() * arrayPublicidadSeccionLateral.length - 1 + 1);
          this.publicidadSeccion3 = arrayPublicidadSeccionLateral[numPubli];

          numPubli = Math.floor(Math.random() * arrayPublicidadSeccionSuperior.length - 1 + 1);
          this.publicidadSeccion4 = arrayPublicidadSeccionSuperior[numPubli];
        }
      });
    }
  }

  /**
   * Actualiza las metaetiquetas de la página con información relevante del post actual.
   * Genera un array de metaetiquetas que incluye datos como descripción, título, autor,
   * editor, y otros metadatos relacionados con el contenido del post.
   *
   * Las metaetiquetas generadas incluyen tanto etiquetas estándar como etiquetas específicas
   * para Open Graph (og) y Dublin Core (DC).
   *
   * @private
   */
  updateMetaTags(titulo): void {
    const metaTagsArray: MetaDefinition[] = [
      { name: "description", content: "Noticias de " + titulo },
      { property: "og:title", content: titulo },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${this.constantesGlobales.siteDomain}/assets/images/${this.constantesGlobales.logoMain}` },
      { property: "og:url", content: this.platformService.getLocationUrl() },
      { property: "og:description", content: "Noticias de " + titulo },
      { property: "og:site_name", content: this.constantesGlobales.siteDomain },
    ];

    this.canonicalService.updateCanonicalURls(metaTagsArray);
  }

  titleCaseWord(text: string): string {
    if (!text) {
      return text;
    }
    const arrayText = text.split(" ");
    text = "";
    for (const word of arrayText) {
      if (word) {
        text += word[0].toUpperCase() + word.substr(1).toLowerCase() + " ";
      }
    }
    return text;
  }

  // assignClass(i: number) {
  //   if ((this.publicidadSeccion1 === undefined && i === 2) || (this.publicidadSeccion1 === undefined && i === 1)){
  //     return 'div' + (i + 1) + 'SinPubli';
  //   } else if (i === 6 && this.publicidadSeccion2 === undefined ) {
  //     return 'div' + (i + 1) + 'SinPubli';
  //   } else if ((this.publicidadSeccion4 === undefined && i === 15) || (this.publicidadSeccion4 === undefined && i === 16) ) {
  //     return 'div' + (i + 1) + 'SinPubli';
  //   } else {
  //     return 'div' + (i + 1);
  //   }
  // }

  trackByItem(index: number, item: any) {
    return item.id;
  }

  private initGfk(titulo): void {
    if (this.constantesGlobales.gfk === true) {
      if (this.platformService.isBrowser()) {
        const customParams = {
          c1: this.stripProtocolAndSubdomain(this.constantesGlobales.siteDomain),
          c2: titulo,
        };
        this.gfkService.impression(customParams);
      }
    }
  }

  stripProtocolAndSubdomain(url: string): string {
    const strippedUrl = url?.replace(/^(https?:\/\/)?(www\.)?/, "");
    return strippedUrl;
  }
}
